import { ReactNode, useState } from 'react';
import { BarChart3, Boxes, BriefcaseBusiness, CalendarCheck2, FileSearch, HandCoins, LogOut, Megaphone, Menu, Store, TrendingUp, X } from 'lucide-react';
import { api } from './api';
import { User } from './App';
import { officeLink } from './office-navigation';
import { officeAgendas, officeModuleDestination, OfficeModule } from '../server/office-agendas';

export type ModuleKey = OfficeModule;

const modules = {
  finance: { label: 'Finance', subtitle: 'Cashflow a hospodaření', icon: BarChart3 },
  attendance: { label: 'Docházka', subtitle: 'Čas a výkonnost', icon: CalendarCheck2 },
  marketing: { label: 'Marketing', subtitle: 'Kampaně a návratnost', icon: Megaphone },
  payroll: { label: 'Mzdy', subtitle: 'Podklady a výplaty', icon: HandCoins },
  hr: { label: 'Personalistika', subtitle: 'Dovolená a lidé', icon: BriefcaseBusiness },
  reports: { label: 'Zaměstnanecké reporty', subtitle: 'Cíle a podklady bonusů', icon: BarChart3 },
  procurement: { label: 'Zakázky', subtitle: 'Veřejné zakázky', icon: FileSearch },
  wholesale: { label: 'Velkoobchod', subtitle: 'Partneři a akvizice', icon: Store },
  marketplaces: { label: 'Marketplace', subtitle: 'Prodejní kanály', icon: Boxes },
  pricing: { label: 'Cenový automat', subtitle: 'Ceny a optimalizace zisku', icon: TrendingUp },
} as const;

function can(user: User, permissions: readonly string[]): boolean {
  return user.permissions.includes('SuperAdmin') || permissions.some(permission => user.permissions.includes(permission));
}

export function Shell({ user, activeModule, onLogout, children }: {
  user: User; activeModule: ModuleKey; onLogout: () => void; children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const active = modules[activeModule];
  async function logout() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    onLogout();
  }

  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-brand"><span className="brand-cube">S</span><div><strong>SHX</strong><small>OFFICE</small></div><button className="mobile-close" onClick={() => setOpen(false)}><X /></button></div>
        <nav>
          <span className="nav-label">Agendy</span>
          {(Object.entries(modules) as Array<[ModuleKey, typeof modules[ModuleKey]]>).map(([key, item]) => {
            const Icon = item.icon;
            if (!can(user, officeAgendas[key].permissions)) return null;
            const href = ['localhost', '127.0.0.1'].includes(location.hostname) ? `/?module=${key}` : officeLink(officeModuleDestination(key));
            return <a key={key} href={href} className={key === activeModule ? 'active' : ''}><Icon size={21} /><span><strong>{item.label}</strong><small>{item.subtitle}</small></span></a>;
          })}
        </nav>
        <div className="sidebar-user"><div className="avatar">{user.identifier.slice(0, 2).toUpperCase()}</div><div><strong>{user.identifier}</strong><small>Vendure účet</small></div><button title="Odhlásit" onClick={logout}><LogOut size={18} /></button></div>
      </aside>
      {open && <button aria-label="Zavřít menu" className="sidebar-backdrop" onClick={() => setOpen(false)} />}
      <main className="workspace">
        <header className="topbar"><button className="menu-button" onClick={() => setOpen(true)}><Menu /></button><div><span className="eyebrow">SHX Office</span><h1>{active.label}</h1></div><div className="live-pill"><span /> Vlastní databáze</div></header>
        <div className="workspace-content">{children}</div>
      </main>
    </div>
  );
}
